import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { DynamoDBService } from './dynamodb.service';
import { Storage } from '@ionic/storage-angular';
import { DeviceRegistryService } from './device-registry.service';
import { GetUserBuildingGQL } from 'src/graphql/generated/schema';

export interface CachedUserProfile {
  userId: string;
  name?: string;
  email?: string;
  phone?: string;
  buildingId?: string;
  buildingName?: string;
  floor?: string;
  unitNumber?: string;
  unitName?: string;
  bedrooms?: number;
  bathrooms?: number;
  lastUpdated: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserProfileCacheService {
  private readonly STORAGE_KEY = 'cached_user_profile';
  private userProfileSubject = new BehaviorSubject<CachedUserProfile | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();
  private storageInitialized = false;

  constructor(
    private dynamoDBService: DynamoDBService,
    private storage: Storage,
    private deviceRegistry: DeviceRegistryService,
    private getUserBuildingGQL: GetUserBuildingGQL
  ) {}

  private async ensureStorageInitialized(): Promise<void> {
    if (!this.storageInitialized) {
      await this.storage.create();
      this.storageInitialized = true;
    }
  }

  async initializeCache(): Promise<void> {
    try {
      // Ensure storage is initialized
      await this.ensureStorageInitialized();
      
      // Load cached profile from storage
      const cachedProfile = await this.storage.get(this.STORAGE_KEY);
      if (cachedProfile) {
        // console.log('Loaded cached user profile:', cachedProfile);
        this.userProfileSubject.next(cachedProfile);
      }
    } catch (error) {
      console.error('Error loading cached user profile:', error);
    }
  }

  async refreshUserProfile(userId: string): Promise<CachedUserProfile | null> {
    try {
      // console.log('Refreshing user profile for:', userId);

      // Fetch user profile from DynamoDB and building/unit info from GraphQL
      const [userProfile, buildingResult] = await Promise.all([
        this.dynamoDBService.getUserProfile(),
        firstValueFrom(this.getUserBuildingGQL.fetch())
      ]);

      const building = (buildingResult as any).data?.getUserBuilding;
      const unit = building?.unit;

      // Combine the data
      const cachedProfile: CachedUserProfile = {
        userId,
        name: userProfile.name,
        email: userProfile.email,
        phone: userProfile.phone,
        buildingId: building?.id,
        buildingName: building?.name,
        floor: unit?.floor,
        unitNumber: unit?.unitNumber,
        unitName: unit?.name,
        bedrooms: unit?.bedrooms,
        bathrooms: unit?.bathrooms,
        lastUpdated: new Date().toISOString()
      };

      // console.log('User profile refreshed:', cachedProfile);

      // Update device registry with unit info
      if (unit?.unitNumber) {
        await this.deviceRegistry.updateUnitInfo(
          unit.unitNumber,
          unit.bedrooms,
          unit.bathrooms
        );
      }

      // Update cache and storage
      this.userProfileSubject.next(cachedProfile);
      await this.ensureStorageInitialized();
      await this.storage.set(this.STORAGE_KEY, cachedProfile);

      return cachedProfile;
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  }

  getCachedProfile(): CachedUserProfile | null {
    return this.userProfileSubject.value;
  }

  async clearCache(): Promise<void> {
    try {
      await this.ensureStorageInitialized();
      await this.storage.remove(this.STORAGE_KEY);
      this.userProfileSubject.next(null);
      console.log('User profile cache cleared');
    } catch (error) {
      console.error('Error clearing user profile cache:', error);
    }
  }

  // Check if cached profile needs refresh (older than 24 hours)
  needsRefresh(): boolean {
    const profile = this.userProfileSubject.value;
    if (!profile || !profile.lastUpdated) {
      return true;
    }

    const lastUpdated = new Date(profile.lastUpdated);
    const now = new Date();
    const hoursSinceUpdate = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
    
    return hoursSinceUpdate > 24;
  }
}